import { useEffect, useId, useState, type FormEvent } from "react";
import { ApiError, postTestimonial, uploadTestimonialPhoto } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { useBotProtection } from "../../hooks/useBotProtection";
import { HoneypotField } from "./HoneypotField";
import { TurnstileWidget } from "./TurnstileWidget";

const MAX_PHOTOS = 3;

type PendingPhoto = {
  id: string;
  url: string;
  name: string;
};

export function TestimonialForm({ onSuccess }: { onSuccess?: () => void }) {
  const fileInputId = useId();
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [body, setBody] = useState("");
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const {
    config,
    needsTurnstile,
    ready,
    website,
    setWebsite,
    botFields,
    onTurnstileToken,
    onTurnstileExpire,
    registerTurnstileReset,
    resetBotProtection,
  } = useBotProtection();

  useEffect(() => {
    if (user?.name && !name) setName(user.name);
  }, [user?.name, name]);

  async function onPickPhotos(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    e.target.value = "";
    if (!files?.length || busy || uploadingPhoto) return;

    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      setError(`You can attach up to ${MAX_PHOTOS} photos.`);
      return;
    }

    const toUpload = Array.from(files).slice(0, remaining);
    setUploadingPhoto(true);
    setError(null);

    try {
      for (const file of toUpload) {
        if (!file.type.startsWith("image/")) {
          setError("Please choose image files only.");
          continue;
        }
        const data = await uploadTestimonialPhoto(file);
        setPhotos((prev) => [
          ...prev,
          { id: `${Date.now()}-${Math.random()}`, url: data.uploadsUrl, name: file.name },
        ]);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  }

  function removePhoto(id: string) {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!ready || uploadingPhoto) return;
    setError(null);
    setBusy(true);
    try {
      await postTestimonial({
        name: name.trim(),
        location: location.trim() || undefined,
        body: body.trim(),
        ...(photos.length ? { photoUrls: photos.map((p) => p.url) } : {}),
        ...botFields,
      });
      setDone(true);
      onSuccess?.();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Could not save your story");
      resetBotProtection();
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="form-success">
        <strong>Thank you.</strong> Your story is awaiting review and will appear on this page once
        approved.
      </div>
    );
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="form-stack testimonial-form">
      <HoneypotField value={website} onChange={setWebsite} />
      <label>
        <span>Your name</span>
        <input
          type="text"
          required
          maxLength={120}
          placeholder="How you'd like to be credited"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={busy}
        />
      </label>
      <label>
        <span>Where you're writing from (optional)</span>
        <input
          type="text"
          maxLength={120}
          placeholder="e.g. Kampala · attended 3 nights"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          disabled={busy}
        />
      </label>
      <label>
        <span>Your story</span>
        <textarea
          required
          minLength={10}
          maxLength={600}
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={busy}
          placeholder="A line or two on what God did in the room…"
        />
        <span className="form-help">{body.length} / 600</span>
      </label>

      <div className="testimonial-photo-field">
        <span className="testimonial-photo-label">Photos (optional, up to {MAX_PHOTOS})</span>
        <p className="form-help testimonial-photo-help">
          Add a photo from the room — wristband, worship moment, or anything that helps us verify
          your story.
        </p>
        {photos.length > 0 && (
          <ul className="testimonial-photo-previews">
            {photos.map((p) => (
              <li key={p.id}>
                <img src={p.url} alt="" loading="lazy" />
                <button
                  type="button"
                  className="testimonial-photo-remove"
                  onClick={() => removePhoto(p.id)}
                  disabled={busy || uploadingPhoto}
                  aria-label={`Remove ${p.name}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        {photos.length < MAX_PHOTOS && (
          <label htmlFor={fileInputId} className="testimonial-photo-add">
            <input
              id={fileInputId}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
              multiple
              onChange={(ev) => void onPickPhotos(ev)}
              disabled={busy || uploadingPhoto}
            />
            {uploadingPhoto ? "Uploading…" : photos.length ? "Add another photo" : "Choose photo(s)"}
          </label>
        )}
      </div>

      {needsTurnstile && config?.turnstileSiteKey && (
        <TurnstileWidget
          siteKey={config.turnstileSiteKey}
          onToken={onTurnstileToken}
          onExpire={onTurnstileExpire}
          onRegisterReset={registerTurnstileReset}
        />
      )}

      {error && <div className="form-error">{error}</div>}

      <button type="submit" className="btn btn-primary" disabled={busy || !ready || uploadingPhoto}>
        {busy ? "Sending…" : "Submit for review"}
      </button>
    </form>
  );
}
